import Data from '@/data'
import {
  itemAddSocket,
  itemFormatProperties,
  itemGetMods,
  itemGetName,
  itemGetProperties,
  itemGetStats,
  itemMaxSockets,
  itemParseMods,
  itemRemoveSocket,
  itemResetMods
} from '@/logic/item'
import { getGemColor } from '@/logic/mods'
import { FormatLines } from '@/tooltip/common'
import { CallbackWrap } from '@/types/common'
import { Gem, Item, StatType } from '@/types/item'
import Popup from '@/ui/popup'
import { ErrorPopup } from '@/ui/utils/Overlay'
import { OverlayContext } from '@/ui/utils/OverlayManager'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React, { ReactNode } from 'react'
import { GemEditor } from '../embeds/SkillEditor'
import { ItemOptions } from './ItemOptions'

import { EmbedLevel } from '@/common'
import Store from '@/store'
import Session from '@/store/session'
import { WithItemTooltip } from '@/tooltip'
import { ItemClusterJewel } from './ItemClusterJewel'
import './ItemEditor.scss'
import { ModCategory } from './ItemMods'
import { StatCategory } from './ItemStats'
import { UniqueCategory, UniqueCustomCategory, UniqueModCategory } from './ItemUnique'

export type ItemProps = {
  item: Item
  onChange: (value: Item | ((prev: Item) => Item)) => void
}

function ItemInfo({ item }: { item: Item }) {
  const format = React.useMemo(() => {
    const stats = itemGetStats(item)
    const props = itemGetProperties(item, stats)
    return itemFormatProperties(item, props)
  }, [item])

  if (!format.properties.length && !format.requirements.length) {
    return null
  }
  return (
    <div className="poe-item-panel poe-item-info">
      <FormatLines lines={format.properties} />
      <FormatLines lines={format.requirements} />
    </div>
  )
}

function ItemCategory({
  item,
  onChange,
  type,
  mods
}: {
  type: StatType | 'custom'
  mods: ReturnType<typeof itemGetMods>
} & ItemProps) {
  if (item.mods) {
    if (type === 'custom') return null
    if (item.unique) return <UniqueModCategory item={item} onChange={onChange} type={type} mods={mods} />
    if (!mods[type] || !Object.keys(mods[type]!).length) return null
    return <ModCategory item={item} onChange={onChange} type={type} />
  } else {
    return <StatCategory item={item} onChange={onChange} type={type} />
  }
}

function SocketColorPicker({ onSelect }: { onSelect: (value: string) => void }) {
  return (
    <div className="poe-SocketColorPicker">
      <div className="poe-socket poe-socket-red" onClick={() => onSelect('red')} />
      <div className="poe-socket poe-socket-green" onClick={() => onSelect('green')} />
      <div className="poe-socket poe-socket-blue" onClick={() => onSelect('blue')} />
      <div className="poe-socket poe-socket-white" onClick={() => onSelect('white')} />
    </div>
  )
}

function ItemSocket({
  item,
  onChange,
  index
}: {
  index: number
} & ItemProps) {
  const [showColors, setShowColors] = React.useState<DOMRect>()
  const data = item.sockets[index]
  const gemData = data.gem && Data.gems[data.gem.id]
  const onRemove = React.useCallback(() => onChange((item) => itemRemoveSocket(item, index)), [onChange, index])
  const onSetColor = React.useCallback(
    (value: string) => {
      setShowColors(undefined)
      onChange((item) => {
        item = { ...item }
        item.sockets = item.sockets.slice()
        item.sockets[index] = { ...item.sockets[index], color: value }
        return item
      })
    },
    [onChange, index]
  )
  const onChangeGem = React.useCallback(
    (value: CallbackWrap<Gem | undefined>) =>
      onChange((item) => {
        const prev = item.sockets[index].gem
        if (typeof value === 'function') value = value(prev)
        if (value === prev) return item
        item = { ...item }
        item.sockets = item.sockets.slice()
        const data = (item.sockets[index] = {
          ...item.sockets[index],
          gem: value
        })
        const gemData = data.gem && Data.gems[data.gem.id]
        const color = gemData && getGemColor(gemData)
        if (color && color !== 'white' && data.color !== 'white' && data.color !== color) {
          data.color = color
        }
        return item
      }),
    [onChange, index]
  )
  const onToggleLink = React.useCallback(
    () =>
      onChange((item) => {
        item = { ...item }
        item.sockets = item.sockets.slice()
        item.sockets[index] = {
          ...item.sockets[index],
          link: !item.sockets[index].link
        }
        return item
      }),
    [onChange, index]
  )
  const socketRef = React.useRef<HTMLDivElement>(null)
  const onShowColors = React.useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).classList.contains('poe-socket-link')) return
      setShowColors(socketRef.current!.getBoundingClientRect())
    },
    [socketRef]
  )
  return (
    <li className="poe-socket">
      <div className="poe-remove">
        {item.sockets.length > 1 && <FontAwesomeIcon icon={faTimes} onClick={onRemove} />}
      </div>
      <div
        className={classNames(
          `poe-SocketImage poe-socket-${data.color}`,
          gemData && `poe-gem-${getGemColor(gemData)}`,
          { 'poe-socket-support': gemData && gemData.is_support }
        )}
        onClick={onShowColors}
        ref={socketRef}>
        <div className="poe-image" />
        {index < item.sockets.length - 1 && (
          <div
            className={classNames('poe-socket-link', {
              'poe-active': data.link
            })}
            onClick={onToggleLink}
          />
        )}
      </div>
      <GemEditor gem={data.gem} onChange={onChangeGem} />
      {!!showColors && (
        <Popup alignPosition={showColors} alignPadding={0} onClose={() => setShowColors(undefined)}>
          <SocketColorPicker onSelect={onSetColor} />
        </Popup>
      )}
    </li>
  )
}

function ItemSockets({ item, onChange }: ItemProps) {
  const maxSockets = React.useMemo(() => itemMaxSockets(item).normal, [item])
  const onAddSocket = React.useCallback(() => onChange((item) => itemAddSocket(item)), [onChange])
  return (
    <ul className="poe-ItemSockets">
      {!!item.sockets &&
        item.sockets.map((_, index) => <ItemSocket key={index} index={index} item={item} onChange={onChange} />)}
      {(!item.sockets || item.sockets.length < maxSockets) && (
        <li className="poe-add-socket" onClick={onAddSocket}>
          Add Socket
        </li>
      )}
    </ul>
  )
}

function ItemAbyssSocket({ item, onSelect, index }: { item: Item; index: number; onSelect: (index: number) => void }) {
  const jewel = Store.useItem(item.jewels?.[index])
  const content = (
    <li className="poe-socket" onClick={() => onSelect(index)}>
      <div className={classNames(`poe-SocketImage poe-socket-abyss`, jewel && `poe-gem-abyss`)}>
        <div className="poe-image" />
      </div>
      {jewel ? (
        <div className={`poe-abyss-jewel poe-item-${jewel.rarity}`}>{itemGetName(jewel)}</div>
      ) : (
        <div className="poe-abyss-jewel poe-empty">Select Jewel...</div>
      )}
    </li>
  )
  return jewel ? (
    <WithItemTooltip item={jewel} align="left">
      {content}
    </WithItemTooltip>
  ) : (
    content
  )
}

function ItemJewels({ item, onSelect }: { item: Item; onSelect: (index: number) => void }) {
  return (
    <ul className="poe-ItemSockets">
      {[...(item.jewels ?? [])].map((_, index) => (
        <ItemAbyssSocket key={index} index={index} item={item} onSelect={onSelect} />
      ))}
    </ul>
  )
}

export default function ItemEditor({
  item,
  onChange,
  header,
  preview,
  noStats,
  noGems,
  noBase,
  onSelectJewel,
  options
}: {
  header?: ReactNode
  preview?: ReactNode
  noStats?: boolean
  noGems?: boolean
  noBase?: boolean
  onSelectJewel?: (index: number) => void
  options?: ReactNode
} & ItemProps) {
  const isCC = Session.useSelector((state) => (state.user?.active ?? 0) >= EmbedLevel)

  const [editMode, setEditMode] = React.useState<'gems' | 'jewels'>()
  const overlays = React.useContext(OverlayContext)

  const maxSockets = React.useMemo(() => itemMaxSockets(item), [item])

  const hasGems = maxSockets.normal > 0 && !noGems
  const hasJewels = Boolean(onSelectJewel && maxSockets.abyss > 0)
  const mode =
    editMode === 'gems' && hasGems
      ? 'gems'
      : editMode === 'jewels' && hasJewels
      ? 'jewels'
      : !item || item.mods
      ? 'mods'
      : 'stats'
  const setMods = React.useCallback(() => {
    setEditMode(undefined)
    onChange((item) => {
      if (item.mods) return item
      item = itemParseMods(item)
      if (!item.mods) overlays.async(<ErrorPopup message="Failed to parse item mods" />)
      return item
    })
  }, [onChange, overlays])
  const setStats = React.useCallback(() => {
    setEditMode(undefined)
    onChange((item) => itemResetMods(item))
  }, [onChange])
  const mods = React.useMemo(() => itemGetMods(item, false, true), [item])
  return (
    <div className="poe-ItemEditor">
      <div className="poe-item-header poe-item-panel">
        {preview || null}
        <div className="poe-item-info">
          {header || null}
          <ItemOptions item={item} onChange={onChange} noBase={noBase} children={options} />
        </div>
      </div>
      <ItemInfo item={item} />
      <div className="poe-item-tabs">
        {(!noStats || !item.mods) && (
          <div
            className={classNames('poe-tab', {
              'poe-active': mode === 'stats'
            })}
            onClick={setStats}>
            Stats
          </div>
        )}
        <div className={classNames('poe-tab', { 'poe-active': mode === 'mods' })} onClick={setMods}>
          Mods
        </div>
        {hasGems && (
          <div className={classNames('poe-tab', { 'poe-active': mode === 'gems' })} onClick={() => setEditMode('gems')}>
            Gems
          </div>
        )}
        {hasJewels && (
          <div
            className={classNames('poe-tab', { 'poe-active': mode === 'jewels' })}
            onClick={() => setEditMode('jewels')}>
            Jewels
          </div>
        )}
      </div>
      {mode === 'gems' ? (
        <ItemSockets item={item} onChange={onChange} />
      ) : mode === 'jewels' && onSelectJewel ? (
        <ItemJewels item={item} onSelect={onSelectJewel} />
      ) : (
        <>
          <ItemCategory item={item} onChange={onChange} mods={mods} type="enchant" />
          <ItemClusterJewel item={item} onChange={onChange} />
          <ItemCategory item={item} onChange={onChange} mods={mods} type="implicit" />
          {!!item.unique && <UniqueCategory item={item} onChange={onChange} />}
          <ItemCategory item={item} onChange={onChange} mods={mods} type="fractured" />
          <ItemCategory item={item} onChange={onChange} mods={mods} type="explicit" />
          <ItemCategory item={item} onChange={onChange} mods={mods} type="crafted" />
          {!!(item.unique && !item.mods) && <ItemCategory item={item} onChange={onChange} mods={mods} type="custom" />}
          {!!((item.unique || isCC) && !item.mods) && <UniqueCustomCategory item={item} onChange={onChange} />}
        </>
      )}
    </div>
  )
}
